import { useMemo } from 'react';
import debounce from 'lodash/debounce';
import { WatchQueryFetchPolicy } from '@apollo/client';

import {
  useStudentMutation,
  useStudentQuery,
} from 'lib/apollo/student-apollo-hooks';
import { UPSERT_STUDENT_ACTIVITY_ANSWER } from './student-answer.graphql';
import type { UpsertStudentActivityAnswerInput } from '../../../../gql/schema-types';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import type {
  UpsertStudentActivityAnswerMutation,
  UpsertStudentActivityAnswerMutationVariables,
} from './student-answer.graphql.generated';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { getStudentClassInfo } from 'approot/shared/api/auth/student-user-auth';
import { GET_MY_ANSWERS } from 'approot/activity-builder/present/activity-answers.graphql';
import type {
  GetMyAnswersQuery,
  GetMyAnswersQueryVariables,
} from '../../present/activity-answers.graphql.generated';

type AnswerInput = Omit<
  UpsertStudentActivityAnswerInput,
  'contentId' | 'contentType' | 'contentVersion' | 'classId'
>;

export const useStudentAnswerMutations = (
  onCompleted?: (data: UpsertStudentActivityAnswerMutation) => void
) => {
  const { showError } = useSnackbar();
  const { contentId, contentType, version } = useGetInteractiveLessonParams();

  const [upsertAnswer, { loading, error }] = useStudentMutation<
    UpsertStudentActivityAnswerMutation,
    UpsertStudentActivityAnswerMutationVariables
  >(UPSERT_STUDENT_ACTIVITY_ANSWER, {
    onError: () =>
      showError(
        'There was an error saving your answer. Please try again later.'
      ),
    onCompleted: res => {
      onCompleted?.(res);
    },
  });

  const debounceUpsertAnswer = useMemo(
    () =>
      debounce((input: AnswerInput) => {
        const classId = getStudentClassInfo()?.classId;
        if (classId && version) {
          upsertAnswer({
            variables: {
              input: {
                ...input,
                classId,
                contentType,
                contentId,
                contentVersion: version,
              },
            },
          });
        }
      }, 300),
    //eslint-disable-next-line
    []
  );

  return {
    loading,
    error,
    debounceUpsertAnswer,
  };
};

export const useStudentGetAnswers = ({
  skip,
  fetchPolicy,
  componentId,
  onCompleted,
}: {
  skip?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
  componentId?: string;
  onCompleted?: (data: GetMyAnswersQuery) => void;
}) => {
  const classId = getStudentClassInfo()?.classId;

  const { contentId, contentType, version } = useGetInteractiveLessonParams();

  const { showError } = useSnackbar();
  const { data, loading, variables, error, client } = useStudentQuery<
    GetMyAnswersQuery,
    GetMyAnswersQueryVariables
  >(GET_MY_ANSWERS, {
    fetchPolicy,
    skip: !classId || !version || skip,
    variables: {
      input: {
        contentId,
        contentType,
        classId: classId!,
        contentVersion: version!,
        componentId,
      },
    },
    onCompleted: res => {
      onCompleted?.(res);
    },
    onError: () => {
      showError(
        'There was an error loading your answers. Please refresh and try again.',
        {
          autoHide: false,
        }
      );
    },
  });

  return {
    data,
    loading,
    error,
    variables,
    hasFetchedAnswers: !!(error || data?.getMyAnswers),
    client,
    componentAnswers: data?.getMyAnswers.filter(
      a => a.componentId === componentId
    ),
  };
};
